
// Design System
import { Message } from 'jl-design-system/components/message/Message';
import { Modal } from 'jl-design-system/components/modal/Modal';
import { PrimaryButton } from 'jl-design-system/elements/button/Button';

// Types
import { CollectionPointProps } from 'types/CollectionPoint.types';
import { ClickAndCollectStoreMapViewProps } from './ClickAndCollectStoreMapView.types';

// Config
import { useWindowSize } from '@context/window/WindowSizeContext';
import getClickCollectFormattedTime from '@utils/helpers/time';

// Components
import ClickAndCollectDisplayOpeningHours from '@components/click-and-collect-display-opening-hours';
import Container from '@components/container';
import DisplayAddress from '@components/display-address/DisplayAddress';
import GoogleMap from '@components/google-map/GoogleMap';

// Styles
import styles from './click-and-collect-store-map-view.scss';

const ClickAndCollectStoreMapView = ({
  activeMarker,
  forPrint = false,
  getCollectionPointDatesApiCallActive = false,
  handleMarkerClick = () => {},
  handleSelectShopClick = () => {},
  hideMapInStoreDetailsModal = false,
  hideTimeAndPrice = false,
  markers = [],
  onClose,
  searchTerm = '',
  showSelectShopButton = false,
}: ClickAndCollectStoreMapViewProps) => {
  const { isLargeDesktop } = useWindowSize();

  const getCollectionInfo = (hideTimeAndPrice: boolean, store?: CollectionPointProps) => {
    const {
      collectionChargeFormatted,
      collectionTime,
    } = store ?? {};

    return !hideTimeAndPrice && (
      <Container hideForPrint>
        <p data-testid="modal-message">
          Collect {getClickCollectFormattedTime({
            time: collectionTime,
          })} for <strong>{collectionChargeFormatted}</strong>
        </p>
      </Container>
    );
  };

  const handleMarkerClickHandler = (marker: CollectionPointProps) => {
    if (showSelectShopButton) {
      handleMarkerClick(marker);
    }
  };

  const renderContent = ({
    headerText = '',
    visibleMarkers = markers,
  }) => {
    const {
      address,
      openingHours = [],
      isAvailable,
      unavailabilityReason = '',
    } = activeMarker ?? {};

    return (
      <div>
        {forPrint && (
          <h2
            className={styles.header}
            data-testid="print-header"
          >
            {headerText}
          </h2>
        )}

        <div className={styles.content}>
          {
            !hideMapInStoreDetailsModal &&
            <GoogleMap
              activeMarker={activeMarker}
              className={styles.map}
              handleMarkerClick={handleMarkerClickHandler}
              isStoreSelectable={showSelectShopButton}
              mapName="click-collect-store"
              markers={visibleMarkers}
              searchTerm={searchTerm}
            />
          }

          <div className={hideMapInStoreDetailsModal ? null : styles.messaging}>
            {!isAvailable ? (
              <Container marginBottom="2">
                <Message
                  dataTestId="map-view-unavailability-message"
                  title={unavailabilityReason}
                  type="information"
                />
              </Container>
            ) : !hideMapInStoreDetailsModal && getCollectionInfo(hideTimeAndPrice, activeMarker)}

            <div className={styles.shopInformation}>
              <DisplayAddress address={address} />
              <div className={styles.timetable}>
                <ClickAndCollectDisplayOpeningHours
                  openingHours={openingHours}
                />
              </div>
            </div>

            {showSelectShopButton && isAvailable && (
              <PrimaryButton
                aria-describedby="modal-header"
                className={styles.selectButton}
                data-testid="click-collect-select-button"
                onClick={handleSelectShopClick}
                submitting={getCollectionPointDatesApiCallActive}
              >
                Select shop
              </PrimaryButton>
            )}
          </div>

        </div>
      </div>
    );
  };

  if (!activeMarker) {
    return null;
  }

  const visibleMarkers: CollectionPointProps[] = showSelectShopButton ? markers : [activeMarker];

  if (forPrint) {
    return (
      <>
        {renderContent({
          visibleMarkers,
          headerText: 'Map and opening times',
        })}
      </>
    );
  }

  return (
    <Modal
      className={styles.modal}
      footer={false}
      isOpen
      onClose={onClose}
      title={!forPrint ? activeMarker.storeName : undefined}
      width={isLargeDesktop ? 'medium' : 'large'}
    >
      {renderContent({
        visibleMarkers,
      })}
    </Modal>
  );
};

export default ClickAndCollectStoreMapView;
